/** @format */

import React, {useMemo, useState} from 'react'

import {useLoadingContext} from '@src/contexts'
import {usePendingField} from '@src/hooks/usePendingField'
import * as toast from '@src/toast'

import {FieldValidation} from '../FieldValidation'
import {useValidationApi} from '../ValidationApi'
import {EditFieldInput} from './EditFieldInput'

type FieldNameInputProps = {
  validation: FieldValidation
}

// FieldNameInput renders an ActionInput for Validation field name.
//
// If any of the validation rules were added by an action,
// it is rendered a read-only.
//
// Otherwise, it is rendered as an editable input
// i.e. allow changing the field name.
export function FieldNameInput(props: FieldNameInputProps) {
  const {validation} = props
  const savedFieldName = validation.fieldName

  const {ready} = useLoadingContext()
  const fieldName = usePendingField(validation.fieldName)
  const api = useValidationApi()

  const otherFieldNames = useMemo(() => {
    const s = new Set()
    for (const {fieldName} of api.listValidations.data) {
      if (fieldName !== savedFieldName) {
        s.add(fieldName)
      }
    }
    return s
  }, [api.listValidations.data, savedFieldName])

  const [isDuplicateFieldName, setIsDuplicateFieldName] = useState(false)

  async function updateFieldName() {
    try {
      await api.renameField(savedFieldName, fieldName.value)
    } catch (err) {
      toast.error(err)
    } finally {
      ready()
    }
  }

  return (
    <EditFieldInput
      label="Field Name"
      value={fieldName.value}
      addedBy={validation.plugins}
      editableModeDescription={
        isDuplicateFieldName ? (
          <p className="text-sm text-red-500">
            Cannot rename the field to{' '}
            <code className="font-semibold">{fieldName.value}</code> because it
            already exists.
          </p>
        ) : (
          <p className="text-sm text-gray-500">
            Must match the form <code className="font-semibold">name</code>{' '}
            attribute.
          </p>
        )
      }
      onChange={event => {
        const v = event.target.value
        fieldName.setValue(v)
        setIsDuplicateFieldName(otherFieldNames.has(v))
      }}
      onSaveButtonClick={updateFieldName}
      saveDisabled={
        !fieldName.isDirty || !fieldName.value.trim() || isDuplicateFieldName
      }
    />
  )
}
